<script setup lang="ts">
import type { FolderNodeModel, Scope } from '../../runtime/services/StorageService';
import StorageService from '../../runtime/services/StorageService';

const scope = defineModel<Scope>('scope');
const destination = defineModel<number>('destination', {
    required: false
});
const expanded = defineModel<number[]>('expanded', {
    default: [0]
});

const props = defineProps<{
    excluded?: number[];
    rootFolder?: boolean;
}>();

const emit = defineEmits<{
    (e: 'onNameChange', item: FolderNodeModel): void;
    (e: 'onRemove', item: FolderNodeModel): void;
}>();

const { $t } = useLocalization();
const { $alert } = useAlerts();

const loading = ref(false);
const nodes = ref<Resource<FolderNodeModel>[]>([]);

const currentDestination = computed(() =>
{
    const found = getNodes().find(el => el.id === destination.value);

    return found ? destination.value : null;
});

const loadNodes = async (expandList?: number[], append?: boolean): Promise<void> =>
{
    try
    {
        loading.value = true;

        const { items } = await StorageService.getFolderNodes(scope.value, expandList ?? expanded.value);

        if (append)
            nodes.value = nodes.value.concat(items);
        else
            nodes.value = items;
    }
    catch
    {
        nodes.value = [];
        $alert.error($t('[[[Nie udało się załadować elementów]]]'));
    }
    finally
    {
        loading.value = false;
    }
};

const getFolders = (parentId?: number): Resource<FolderNodeModel>[] =>
{
    if (parentId)
        return nodes.value.filter(p => p.result.parentId === parentId);
    else
        return nodes.value;
};

const getNodes = (parentId?: number): FolderNodeModel[] =>
{
    const prepend = props.rootFolder
        ? [{
            id: null,
            parentId: null,
            name: $t('[[[Folder główny]]]'),
            root: true
        } as FolderNodeModel]
        : [];

    return prepend.concat(getFolders(parentId).map(p => p.result)).filter(el => !props.excluded?.includes(el.id));
};

const getMenu = async (node: FolderNodeModel): Promise<any[]> =>
{
    const menu = [];

    const { canEdit, canDelete } = nodes.value.find(el => el.result.id === node.id)?.meta ?? {};

    if (canEdit)
        menu.push({ name: $t('[[[Zmień nazwę]]]'), icon: 'icon far fa-pen', click: () => emit('onNameChange', node) });

    if (canDelete)
        menu.push({ name: $t('[[[Usuń]]]'), icon: 'icon far fa-trash', click: () => emit('onRemove', node) });

    return menu;
};

const active = (node: FolderNodeModel): boolean =>
{
    return node.id === currentDestination.value;
};

const onToggle = async (node: FolderNodeModel): Promise<void> =>
{
    if (expanded.value.indexOf(node.id) > -1)
    {
        expanded.value = expanded.value.filter(p => p !== node.id);
    }
    else
    {
        expanded.value.push(node.id);

        if (node.hasChildren && getFolders(node.id).length === 0)
        {
            await loadNodes([node.id], true);
        }
    }
};

const selectNode = (node: FolderNodeModel) =>
{
    if (node.isActive !== false)
        destination.value = node.id;
};

watch(scope, async () =>
{
    await loadNodes();
});

onBeforeMount(async () =>
{
    await loadNodes();
});

const reload = async () =>
{
    await loadNodes();
};

defineExpose({
    reload,
    currentDestination
});
</script>

<template>
    <IdeoTree
        :pages="getNodes()"
        :expanded="expanded"
        :menu="getMenu"
        :active="active"
        @toggle="onToggle"
        @click="selectNode"
    />
</template>
